import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
    ResponsiveContainer,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ReferenceLine,
} from 'recharts';
import { format, parseISO, startOfWeek } from 'date-fns';
import { useBoardChartData } from '../../hooks/useBoardChartData';
import { BaseChartProps } from './types';
import { CHART_COLORS, CHART_STYLES, getCurrencySymbol, formatCompactNumber, formatCurrency } from './chartTheme';
import { useSetChartMetadata } from './ChartContext';

interface WeekData {
    label: string;
    revenue: number;
    expenses: number;
    net: number;
}

export const WeeklyCashSummaryChart: React.FC<BaseChartProps> = () => {
    const { t } = useTranslation();
    const { finance, currencySettings } = useBoardChartData();

    const currencySymbol = useMemo(
        () => getCurrencySymbol(currencySettings?.default),
        [currencySettings?.default]
    );

    const chartData = useMemo((): WeekData[] => {
        if (!finance?.dailyBreakdown) return [];

        const weeks: Record<string, { start: Date; revenue: number; expenses: number }> = {};

        const sortedEntries = Object.entries(finance.dailyBreakdown)
            .sort(([a], [b]) => a.localeCompare(b));

        for (const [date, day] of sortedEntries) {
            const parsed = parseISO(date);
            const weekStart = startOfWeek(parsed, { weekStartsOn: 1 });
            const weekKey = format(weekStart, 'yyyy-MM-dd');
            const metrics = (day as Record<string, unknown>).metrics as Record<string, number> | undefined;

            if (!weeks[weekKey]) {
                weeks[weekKey] = { start: weekStart, revenue: 0, expenses: 0 };
            }
            weeks[weekKey].revenue += metrics?.revenue ?? 0;
            weeks[weekKey].expenses += metrics?.expenses ?? 0;
        }

        return Object.values(weeks)
            .sort((a, b) => a.start.getTime() - b.start.getTime())
            .map((week) => {
                const endDate = new Date(week.start);
                endDate.setDate(endDate.getDate() + 6);
                return {
                    label: `${format(week.start, 'MMM d')}-${format(endDate, 'd')}`,
                    revenue: week.revenue,
                    expenses: -Math.abs(week.expenses),
                    net: week.revenue - week.expenses,
                };
            });
    }, [finance?.dailyBreakdown]);

    const isEmpty = useMemo(() => chartData.length === 0, [chartData]);

    useSetChartMetadata({ isEmpty });

    if (isEmpty) return null;

    return (
        <div className="w-full h-full bg-transparent overflow-hidden flex flex-col">
            <div className="flex-1 min-h-0 p-2">
                <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={chartData} margin={CHART_STYLES.margin.withAxis}>
                        <CartesianGrid
                            vertical={false}
                            stroke={CHART_STYLES.grid.stroke}
                            strokeDasharray={CHART_STYLES.grid.strokeDasharray}
                            strokeWidth={CHART_STYLES.grid.strokeWidth}
                            opacity={CHART_STYLES.grid.opacity}
                        />
                        <XAxis
                            dataKey="label"
                            {...CHART_STYLES.axis}
                        />
                        <YAxis
                            tickFormatter={(v: number) => formatCompactNumber(v, currencySymbol)}
                            {...CHART_STYLES.axis}
                        />
                        <Tooltip
                            contentStyle={CHART_STYLES.tooltip.contentStyle}
                            cursor={CHART_STYLES.tooltip.cursor}
                            itemStyle={CHART_STYLES.tooltip.itemStyle}
                            formatter={(value: number, name: string) => [
                                formatCurrency(Math.abs(value), currencySymbol),
                                name,
                            ]}
                        />
                        <Legend wrapperStyle={{ fontSize: '10px' }} />
                        <ReferenceLine y={0} stroke={CHART_COLORS.gray[400]} strokeDasharray="3 3" />
                        <Bar
                            dataKey="revenue"
                            name={t('charts.revenue', 'Revenue')}
                            fill={CHART_COLORS.revenue}
                            radius={CHART_STYLES.bar.radius}
                            maxBarSize={CHART_STYLES.bar.maxBarSize}
                        />
                        <Bar
                            dataKey="expenses"
                            name={t('charts.expenses', 'Expenses')}
                            fill={CHART_COLORS.expense}
                            radius={[0, 0, 3, 3]}
                            maxBarSize={CHART_STYLES.bar.maxBarSize}
                        />
                        <Bar
                            dataKey="net"
                            name={t('charts.net', 'Net')}
                            fill={CHART_COLORS.primary}
                            radius={CHART_STYLES.bar.radius}
                            maxBarSize={CHART_STYLES.bar.maxBarSize}
                        />
                    </BarChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
};
